/**
 * Shared prepare step for photo uploads: validate size, convert to JPEG in parallel.
 * Does not perform upload; callers use result.prepared with their existing upload logic.
 */

import { convertToJpeg } from './browserPhotoToJpeg';

export interface PrepareOptions {
   /** Skip files larger than this (bytes); add to errors */
   maxSizeBytes?: number;
   /** Use only first N files */
   maxCount?: number;
   /** Optional progress: phase, done count, total */
   onProgress?: (phase: 'converting', done: number, total: number) => void;
}

export interface PrepareResult {
   /** Upload-ready JPEG files (only successful conversions) */
   prepared: File[];
   /** Same length as input; null where conversion/validation failed */
   preparedByIndex: (File | null)[];
   /** Files that failed validation or conversion */
   errors: { file: File; error: string }[];
}

/**
 * Prepares files for upload: validates size (optional) and converts to JPEG in parallel.
 * Returns upload-ready File[] and any validation/conversion errors.
 */
export async function preparePhotosForUpload(
   files: File[],
   options: PrepareOptions = {}
): Promise<PrepareResult> {
   const { maxSizeBytes, maxCount, onProgress } = options;
   const list = maxCount != null ? files.slice(0, maxCount) : files;
   const total = list.length;
   const errors: { file: File; error: string }[] = [];

   const results = await Promise.allSettled(
      list.map(async (file, index) => {
         if (maxSizeBytes != null && file.size > maxSizeBytes) {
            throw new Error(
               `File "${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum is ${(maxSizeBytes / (1024 * 1024)).toFixed(0)}MB.`
            );
         }
         const result = await convertToJpeg(file, () => {
            onProgress?.('converting', index, total);
         });
         const res = await fetch(result.base64);
         const blob = await res.blob();
         const name = file.name.replace(/\.[^.]+$/i, '.jpg') || 'photo.jpg';
         return new File([blob], name, { type: 'image/jpeg' });
      })
   );

   const preparedByIndex: (File | null)[] = results.map((outcome, i) => {
      if (outcome.status === 'fulfilled') return outcome.value;
      errors.push({
         file: list[i],
         error: outcome.reason?.message ?? String(outcome.reason),
      });
      return null;
   });

   const prepared = preparedByIndex.filter((f): f is File => f != null);

   return { prepared, preparedByIndex, errors };
}
